import { useCallback, useRef, useState } from 'react'
import { useEditSummary, useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import { useSummaryContext } from './use-summary-context'

export function useSummaryEdit() {
  const { summary, isExpanded, setExpanded, isEditing, setEditing, edited, setEdited } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { dayLabel } = useSummaryDate(summary)
  const editSummaryMutation = useEditSummary()
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onSaveEdits = useCallback(
    async (updated?: string) => {
      const newSummary = updated ?? edited
      if (!newSummary || !newSummary.length) {
        return
      }
      try {
        const before = {
          characters: fullSummary?.summary.length || 0,
          lines: fullSummary?.summary.split('\n').length || 0,
        }
        const after = {
          characters: newSummary.length,
          lines: newSummary.split('\n').length,
        }
        await editSummaryMutation.mutateAsync({ summaryId: summary.id, summary: newSummary })
        AmplitudeRenderer.trackEvent('Summary Action', {
          type: 'Save',
          timePeriod: dayLabel,
          characters: after.characters,
          lines: after.lines,
        })
      } catch (err) {
        console.error(err)
      }
    },
    [summary.id, fullSummary?.summary, editSummaryMutation, dayLabel, edited],
  )

  const onSaveOrEdit = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()

      const editing = !isEditing
      setEditing(editing)

      // Always expand when entering edit mode
      if (editing) {
        setExpanded(true)
        AmplitudeRenderer.trackEvent('Summary Action', {
          type: 'Edit',
          timePeriod: dayLabel,
          characters: fullSummary?.summary.length || 0,
          lines: fullSummary?.summary.split('\n').length || 0,
        })
      } else {
        if (autoSaveRef.current) {
          clearTimeout(autoSaveRef.current)
          autoSaveRef.current = null
        }
        await onSaveEdits()
      }
    },
    [isEditing, setEditing, isExpanded, fullSummary?.summary, onSaveEdits, dayLabel, setExpanded],
  )

  return {
    isEditing,
    edited,
    setEdited,
    setEditing,
    autoSaveRef,
    onSaveEdits,
    onSaveOrEdit,
    isSaving: editSummaryMutation.isPending,
    saveError: editSummaryMutation.error,
    isSuccess: editSummaryMutation.isSuccess,
  }
}
